import React, { useState, useCallback } from 'react';
import 'leaflet/dist/leaflet.css';
import './App.css';
import MapView from './components/MapView';
import SearchPanel from './components/SearchPanel';
import ScenarioPanel from './components/ScenarioPanel';
import ControlPanel from './components/ControlPanel';
import LayerControl from './components/LayerControl';
import MobileActionBar from './components/MobileActionBar';
import StepRibbon from './components/StepRibbon';
import { queryNearest } from './utils/queryNearest';
import { useBreakpoint } from './utils/useBreakpoint';

export default function App() {
  const bp = useBreakpoint();

  const [magnitude, setMagnitude] = useState(null);
  const [results, setResults] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [layerVisibility, setLayerVisibility] = useState({
    waveheight: true,
    contour: true,
    rupture: true,
  });
  const [searchCoords, setSearchCoords] = useState(null);
  const [searchPin, setSearchPin] = useState(null);
  const [maxCoastal, setMaxCoastal] = useState(null);
  const [layersOpen, setLayersOpen] = useState(() => window.innerWidth > 767);

  const hasResults = results.length > 0;

  const handleMagnitudeSelect = useCallback(mag => {
    setMagnitude(mag);
    setResults([]);
    setSelectedScenario(null);
    setSearchPin(null);
    setMaxCoastal(null);
  }, []);

  const handleMapClick = useCallback(
    async (lat, lon) => {
      setSearchCoords({ lat, lng: lon });
      setSearchPin({ lat, lng: lon });
      if (!magnitude) return;
      try {
        const nearest = await queryNearest(lat, lon, magnitude);
        setResults(nearest);
        setSelectedScenario(nearest[0]?.id ?? null);
      } catch (err) {
        console.error('Nearest query failed:', err);
      }
    },
    [magnitude]
  );

  const handlePanelSearch = useCallback(
    async (lat, lng, options = {}) => {
      const mag = options.magnitude ?? magnitude;
      if (!mag) return;
      if (options.magnitude !== undefined) setMagnitude(options.magnitude);
      setSearchCoords({ lat, lng });
      setSearchPin({ lat, lng, popup: options.popup, place: options.place });
      try {
        const nearest = await queryNearest(lat, lng, mag);
        setResults(nearest);
        setSelectedScenario(nearest[0]?.id ?? null);
      } catch (err) {
        console.error('Panel search failed:', err);
      }
    },
    [magnitude]
  );

  const handleScenarioSelect = useCallback(id => {
    setSelectedScenario(id);
    setMaxCoastal(null);
  }, []);

  const handleToggleLayer = useCallback(
    key => setLayerVisibility(prev => ({ ...prev, [key]: !prev[key] })),
    []
  );

  return (
    <div className="app">
      {bp.isMobile ? (
        <>
          <div className="top-nav">
            <StepRibbon compact />
          </div>
          <ControlPanel
            hasResults={hasResults}
            magnitude={magnitude}
            onMagnitudeSelect={handleMagnitudeSelect}
            searchCoords={searchCoords}
            onSearch={handlePanelSearch}
            results={results}
            selected={selectedScenario}
            onSelect={handleScenarioSelect}
            searchPin={searchPin}
            maxCoastal={maxCoastal}
          />
          <LayerControl
            visibility={layerVisibility}
            onToggle={handleToggleLayer}
            hasScenario={!!selectedScenario}
            variant="popover"
            open={layersOpen}
            onOpenChange={setLayersOpen}
          />
          <MobileActionBar
            layersOpen={layersOpen}
            onToggleLayers={() => setLayersOpen(o => !o)}
            selected={selectedScenario}
          />
        </>
      ) : (
        <>
          <SearchPanel
            magnitude={magnitude}
            onMagnitudeSelect={handleMagnitudeSelect}
            searchCoords={searchCoords}
            onSearch={handlePanelSearch}
          />
          <ScenarioPanel
            results={results}
            selected={selectedScenario}
            onSelect={handleScenarioSelect}
            searchPin={searchPin}
            maxCoastal={maxCoastal}
          />
          <LayerControl
            visibility={layerVisibility}
            onToggle={handleToggleLayer}
            hasScenario={!!selectedScenario}
            variant="corner"
            open={layersOpen}
            onOpenChange={setLayersOpen}
          />
        </>
      )}

      <div className="map-wrapper">
        <MapView
          magnitude={magnitude}
          selectedScenario={selectedScenario}
          layerVisibility={layerVisibility}
          onMapClick={handleMapClick}
          hasClicked={hasResults}
          searchPin={searchPin}
          onMaxCoastal={setMaxCoastal}
        />
      </div>
    </div>
  );
}
